import { useEffect, useRef } from 'react';

export const useSessionWarning = ({
  oktaAuth,
  onWarning,
  isTrigger = false,
  warnBeforeMs = 2 * 60 * 1000, // default: 2 minutes
}) => {
  const timeoutRef = useRef(null);

  useEffect(() => {
    if (!isTrigger) {
      return;
    }

    const scheduleWarning = () => {
      const tokens = oktaAuth.tokenManager.getTokensSync();
      const accessToken = tokens?.accessToken;
      if (!accessToken || !accessToken.expiresAt) {
        return;
      }

      const currentTime = Date.now();
      const expiresAtMs = accessToken.expiresAt * 1000;
      const timeUntilExpiry = expiresAtMs - currentTime;

      // Calculate 6 minutes in milliseconds
      const SIX_MINUTES = 360000;
      const FOUR_MINUTES = 240000;

      const is6minsBefore = timeUntilExpiry < SIX_MINUTES;
      const timeRemaining = Math.floor((timeUntilExpiry - FOUR_MINUTES) / 1000); // Convert to seconds

      if (is6minsBefore) {
        // Already within 6 minutes window, start countdown immediately
        onWarning(Math.max(timeRemaining, 0));
        return;
      }

      // Schedule warning exactly at 6 minutes before expiry
      const timeUntil6MinsBefore = timeUntilExpiry - SIX_MINUTES;

      timeoutRef.current = setTimeout(() => {
        onWarning(120); // Start with exactly 2 minutes when hitting 6-minute mark
      }, timeUntil6MinsBefore);
    };

    scheduleWarning();

    // Optional: re-schedule on renew
    const renewListener = () => {
      clearTimeout(timeoutRef.current);
      scheduleWarning();
    };

    oktaAuth.tokenManager.on('renewed', renewListener);
    oktaAuth.tokenManager.on('expired', renewListener);

    return () => {
      clearTimeout(timeoutRef.current);
      oktaAuth.tokenManager.off('renewed', renewListener);
      oktaAuth.tokenManager.off('expired', renewListener);
    };
  }, [oktaAuth, onWarning, warnBeforeMs, isTrigger]);
};
